import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';

const faqData = [
  {
    category: 'General',
    questions: [
      {
        q: 'What is DeFiBot?',
        a: 'DeFiBot is an AI-powered automated trading terminal for EVM-compatible blockchains. It scans mempools in real-time, detects arbitrage and MEV opportunities, and executes profitable trades in under 200ms across 7 networks including Ethereum, BSC, Arbitrum, Base, Polygon, Optimism, and Avalanche.',
      },
      {
        q: 'Is DeFiBot safe to use?',
        a: 'Absolutely. DeFiBot is 100% non-custodial — your private keys never leave your wallet, and we have zero access to your funds. All smart contracts are audited by CertiK and OpenZeppelin, and we run a $100K bug bounty program. Transactions are routed through Flashbots private relay to prevent sandwich attacks.',
      },
      {
        q: 'Which blockchains are supported?',
        a: 'We support 7 EVM networks: Ethereum, BNB Smart Chain, Polygon, Arbitrum, Optimism, Avalanche, and Base. The profit calculator automatically recommends the best network based on gas costs and available opportunities.',
      },
      {
        q: 'Do I need coding experience?',
        a: 'No. DeFiBot is designed for traders of all levels. Simply choose a network, select a preset (Conservative, Balanced, or Aggressive), connect your wallet, and the AI handles everything. For power users, we offer full API access and advanced configuration.',
      },
      {
        q: 'How is DeFiBot different from other trading bots?',
        a: 'DeFiBot combines AI-powered mempool analysis with sub-200ms execution, multi-DEX arbitrage scanning, and Flashbots MEV protection in a single terminal. Most bots offer only one of these. Our 94.7% win rate and $847M+ total volume speak to the difference.',
      },
      {
        q: 'Is there a mobile app?',
        a: 'DeFiBot currently runs entirely in your browser — no downloads needed. It works on any modern browser (Chrome, Firefox, Safari, Edge) on desktop and mobile. A native mobile app is on our Q3 2026 roadmap.',
      },
    ],
  },
  {
    category: 'Trading & Strategies',
    questions: [
      {
        q: 'What trading modes are available?',
        a: 'Four modes: Mixed (AI auto-switches between strategies, recommended for most users), Arbitrage Only (cross-DEX spread hunting, 0.5-2.5% per trade), MEV Only (mempool scanning for frontrun/sandwich opportunities), and Sniper (instant buys on new token launches).',
      },
      {
        q: 'What is MEV and how does DeFiBot exploit it?',
        a: 'MEV (Maximal Extractable Value) refers to profits that can be captured by reordering or inserting transactions in a block. DeFiBot scans pending transactions in the mempool, identifies profitable opportunities (arbitrage, liquidations), and submits optimized bundles through private relays.',
      },
      {
        q: 'How does the arbitrage scanner work?',
        a: 'The scanner monitors price feeds across 50+ DEXes (Uniswap, SushiSwap, PancakeSwap, Curve, Aerodrome, etc.) simultaneously. When it detects a spread of 0.5-2.5% between pools, it executes atomic swaps in a single transaction to capture the difference.',
      },
      {
        q: 'What is the average ROI?',
        a: 'Users average 127% annual ROI with a 94.7% win rate. The live profit calculator shows real-time estimates per trade, per hour, per day, and per week. Results depend on market conditions, network, trade size, and risk settings.',
      },
      {
        q: 'Can I set stop-loss and take-profit?',
        a: 'Yes. All modes support customizable stop-loss, take-profit, trailing stops, minimum profit thresholds, slippage tolerance, position size limits, and daily loss limits. Advanced settings include mempool depth, bundle retries, execution timeout, and priority multiplier.',
      },
      {
        q: 'What presets are available?',
        a: 'Three presets: Conservative (0.05-0.1 trade size, 0.15% min profit, low gas), Balanced (0.1-0.2 trade size, 0.08% min profit, medium gas), and Aggressive (0.5-1.5 trade size, 0.03% min profit, high gas). Each can be further customized.',
      },
      {
        q: 'How does the AI Trading Agent work?',
        a: 'The AI agent performs 8 continuous tasks: Mempool Analysis, Pattern Detection, Route Optimization, Risk Assessment, Gas Prediction, Spread Scanning, Liquidity Mapping, and Slippage Calculation. It adapts its strategy in real-time based on market conditions.',
      },
    ],
  },
  {
    category: 'Networks & Gas',
    questions: [
      {
        q: 'Which network should I start with?',
        a: 'For beginners, Base is recommended — very low gas (~$0.15/trade) with growing DeFi activity. For larger trade sizes ($1000+), Ethereum offers the deepest liquidity. The profit calculator includes a network recommendation engine that auto-suggests the optimal choice. Recommended starting balance: 0.3 ETH (Ethereum/Arbitrum/Base/Optimism), 1.2 BNB (BNB Chain), 25 AVAX (Avalanche), or 1700 POL (Polygon).',
      },
      {
        q: 'How does gas optimization work?',
        a: 'DeFiBot offers 4 gas strategies: Low (~60% of market gas, slower but cheaper), Medium (market rate, balanced), High (1.8x gas, fast confirmation), and Aggressive (3x gas, priority execution). Gas costs are factored into the profit calculator automatically.',
      },
      {
        q: 'Can I trade on multiple networks simultaneously?',
        a: 'Yes. Professional and Enterprise plans support multi-network bot sessions. The AI agent can run on multiple networks in parallel and even suggest network switches when better opportunities arise ("Switch to Base for +$2.40/hr more profit").',
      },
      {
        q: 'What tokens are supported?',
        a: 'DeFiBot supports 26+ tokens per network — all major tokens with real-time prices from CoinGecko. This includes ETH, BNB, MATIC, USDT, USDC, WBTC, UNI, AAVE, LINK, and many more. New tokens are added regularly.',
      },
    ],
  },
  {
    category: 'Pricing & Payment',
    questions: [
      {
        q: 'Is there a free plan?',
        a: 'Yes! The Starter plan is completely free for 7 days. It includes the AI Trading Agent, 3 trading pairs per network, basic arbitrage scanning, and the profit calculator. No payment required to start.',
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept USDT and USDC on 5 networks: Ethereum (ERC-20), BNB Chain (BEP-20), Polygon, Arbitrum, and Tron (TRC-20). All payments are on-chain for full transparency.',
      },
      {
        q: 'Is it a subscription or one-time payment?',
        a: 'All paid plans (Professional $500, Enterprise $1000) are one-time payments with lifetime access. No recurring fees, no hidden charges. Pay once, trade forever.',
      },
      {
        q: 'Can I upgrade my plan later?',
        a: 'Yes. You can upgrade at any time and only pay the difference. For example, upgrading from Professional to Enterprise costs $500. Contact support for seamless upgrades.',
      },
      {
        q: 'Do you offer refunds?',
        a: 'Due to the nature of instant digital access, we cannot offer refunds. However, the 7-day free Starter plan lets you fully test the platform before purchasing. Our support team is available 24/7 to help maximize your experience.',
      },
    ],
  },
  {
    category: 'Security & Privacy',
    questions: [
      {
        q: 'Does DeFiBot need my private keys?',
        a: 'Never. DeFiBot is fully non-custodial. You sign every transaction directly from your wallet (MetaMask, WalletConnect, etc.). We never access, store, or request your private keys or seed phrases.',
      },
      {
        q: 'How are my trades protected from MEV attacks?',
        a: 'All trades are routed through Flashbots private relay and private transaction pools. This means your pending transactions are invisible to mempool scanners, preventing sandwich attacks and frontrunning.',
      },
      {
        q: 'Are the smart contracts audited?',
        a: 'Yes. Our smart contracts are audited by both CertiK and OpenZeppelin. We maintain a $100K bug bounty program and have had zero security incidents since our 2021 launch. Audit reports are publicly available.',
      },
      {
        q: 'What data do you collect?',
        a: 'Minimal data. We do not store private keys, seed phrases, or detailed transaction history on our servers. All trading logic runs locally in your browser. We only collect basic usage analytics to improve the platform.',
      },
      {
        q: 'What wallets are supported?',
        a: 'MetaMask, WalletConnect, Coinbase Wallet, Trust Wallet, and all WalletConnect-compatible wallets. No special permissions beyond standard transaction signing are required.',
      },
    ],
  },
  {
    category: 'Technical & API',
    questions: [
      {
        q: 'Is there an API?',
        a: 'Yes. Professional plans include 100K API calls/month. Enterprise plans get unlimited access. The REST API lets you build custom integrations, automate strategies, and access real-time market data programmatically.',
      },
      {
        q: 'What are the system requirements?',
        a: 'DeFiBot runs entirely in your browser — no downloads or installations. Any modern browser (Chrome, Firefox, Safari, Edge) on desktop or mobile works. Recommended: stable internet connection for real-time mempool scanning.',
      },
      {
        q: 'How fast are trades executed?',
        a: 'Average execution speed is under 200ms from opportunity detection to transaction submission. Our infrastructure uses optimized RPC nodes and direct node connections for each network to minimize latency.',
      },
      {
        q: 'Can I run DeFiBot 24/7?',
        a: 'Yes. Once configured and started, the AI agent runs continuously as long as your browser tab is open. Enterprise users get dedicated server nodes for true 24/7 operation without needing a browser open.',
      },
    ],
  },
];

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredFaq = faqData.map((category) => ({
    ...category,
    questions: category.questions.filter(
      (item) =>
        item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.questions.length > 0);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="faq-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">Support</span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-[var(--text-secondary)]">
            Everything you need to know about DeFiBot
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--text-secondary)]" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)]"
              data-testid="faq-search"
            />
          </div>
        </motion.div>

        {/* Category quick nav */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-2 mb-10"
        >
          {faqData.map((cat) => (
            <button
              key={cat.category}
              onClick={() => document.getElementById(`faq-${cat.category.toLowerCase().replace(/\s+/g, '-')}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="px-4 py-2 text-sm rounded-lg bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/30 transition-all"
            >
              {cat.category}
            </button>
          ))}
        </motion.div>

        <div className="space-y-10">
          {filteredFaq.map((category, categoryIndex) => (
            <motion.div
              key={category.category}
              id={`faq-${category.category.toLowerCase().replace(/\s+/g, '-')}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + categoryIndex * 0.05 }}
            >
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">{category.category}</h2>
              <div className="space-y-3">
                {category.questions.map((item, questionIndex) => {
                  const key = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={questionIndex} className="rounded-xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] overflow-hidden">
                      <button
                        onClick={() => toggleItem(categoryIndex, questionIndex)}
                        className="w-full flex items-center justify-between p-5 text-left"
                        data-testid={`faq-item-${categoryIndex}-${questionIndex}`}
                      >
                        <span className="font-medium text-[var(--text-primary)] pr-4">{item.q}</span>
                        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <CaretDown size={20} className="text-[var(--text-secondary)]" />
                        </motion.div>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="px-5 pb-5 pt-0">
                              <p className="text-[var(--text-secondary)] leading-relaxed">{item.a}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 p-8 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-center"
        >
          <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Still have questions?</h3>
          <p className="text-[var(--text-secondary)] mb-6">Our support team is here to help you 24/7</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-primary)] text-[var(--bg-primary)] font-semibold rounded-xl hover:shadow-[0_0_20px_var(--border-glow)] transition-all"
          >
            Contact Support
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
